// frontend/src/components/TenantInfoForm.jsx
//
// Option-2 + Option-A refactor: TenantInfoForm is the TAB CONTAINER and the
// single owner of shared tenant state. Public shape is unchanged (default
// export, no props) so CrmDashboard routing and the Sidebar link keep working.
//
// WHY STATE LIVES HERE (Option A): the backend PUT /api/tenants/:id does a
// FULL-COLUMN update -- it overwrites every column from the payload. A partial
// save would blank unsent fields. So both the General and Invoice tabs must
// submit the COMPLETE tenant object. To guarantee each Save always carries
// current values from BOTH tabs, the tenant form state is owned here and shared
// down to both tabs. Each tab has its own Save button, but both call the same
// onSave, which PUTs the full object. This is the shared-state requirement that
// makes Option A safe (no stale-overwrite, no blanked fields).
//
// The Email Sender tab is self-contained (its own endpoints/state) and does not
// share this state.

import React, { useEffect, useState } from "react";
import secureAxios from "../utils/secureAxios";
import { formatPhone } from "../utils/formatPhone";
import TenantGeneralInfoTab from "./TenantGeneralInfoTab";
import TenantInvoiceSettingsTab from "./TenantInvoiceSettingsTab";
import TenantEmailSenderTab from "./TenantEmailSenderTab";

const TenantInfoForm = () => {
  const [activeTab, setActiveTab] = useState("general");

  const [formData, setFormData] = useState({
    tenant_name: "",
    phone_number: "",
    address_line_1: "",
    address_line_2: "",
    city: "",
    state_id: "",
    postal_code: "",
    logo_url: "",
    enable_invoices: false,
    invoice_number_prefix: "INV",
    invoice_notes: "",
  });

  const [states, setStates] = useState([]);
  const [message, setMessage] = useState("");
  const [logoUploadMessage, setLogoUploadMessage] = useState("");
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [userInfo, setUserInfo] = useState(null);
  const [uploadingLogo, setUploadingLogo] = useState(false);
  const [displayLogoUrl, setDisplayLogoUrl] = useState("");

  const fetchLogoUrl = async (rawLogoUrl, tenantId) => {
    if (!rawLogoUrl || !tenantId) {
      setDisplayLogoUrl("");
      return;
    }
    if (rawLogoUrl.startsWith("http://") || rawLogoUrl.startsWith("https://")) {
      setDisplayLogoUrl(rawLogoUrl);
      return;
    }
    try {
      const response = await secureAxios.get(`/api/tenants/${tenantId}/logo`);
      setDisplayLogoUrl(response.data.logo_url);
    } catch (err) {
      console.error("Error fetching logo URL:", err);
      setDisplayLogoUrl("");
    }
  };

  useEffect(() => {
    const fetchData = async () => {
      try {
        setLoading(true);

        const sessionResponse = await secureAxios.get("/api/auth/profile");

        if (!sessionResponse.data || !sessionResponse.data.tenant_id) {
          setError("Session expired. Please log in again.");
          setLoading(false);
          return;
        }

        const currentUser = sessionResponse.data;
        setUserInfo(currentUser);

        if (!currentUser.is_admin) {
          setError("You must be an administrator to access this page.");
          setLoading(false);
          return;
        }

        const tenantResponse = await secureAxios.get(
          `/api/tenants/${currentUser.tenant_id}`
        );

        const tenantData = {
          tenant_name: tenantResponse.data.tenant_name || "",
          phone_number: formatPhone(tenantResponse.data.phone_number || ""),
          address_line_1: tenantResponse.data.address_line_1 || "",
          address_line_2: tenantResponse.data.address_line_2 || "",
          city: tenantResponse.data.city || "",
          state_id: tenantResponse.data.state_id || "",
          postal_code: tenantResponse.data.postal_code || "",
          logo_url: tenantResponse.data.logo_url || "",
          enable_invoices: tenantResponse.data.enable_invoices || false,
          invoice_number_prefix:
            tenantResponse.data.invoice_number_prefix || "INV",
          invoice_notes: tenantResponse.data.invoice_notes || "",
        };

        setFormData(tenantData);

        if (tenantData.logo_url) {
          await fetchLogoUrl(tenantData.logo_url, currentUser.tenant_id);
        }

        setError(null);
      } catch (err) {
        console.error("Error loading tenant information:", err.message);
        setError("Error loading tenant information.");
      } finally {
        setLoading(false);
      }
    };

    fetchData();
  }, []);

  useEffect(() => {
    secureAxios
      .get("/api/states")
      .then((res) => setStates(res.data))
      .catch((err) => console.error("Error loading states", err));
  }, []);

  useEffect(() => {
    if (message) {
      const timer = setTimeout(() => setMessage(""), 4000);
      return () => clearTimeout(timer);
    }
  }, [message]);

  useEffect(() => {
    if (logoUploadMessage) {
      const timer = setTimeout(() => setLogoUploadMessage(""), 4000);
      return () => clearTimeout(timer);
    }
  }, [logoUploadMessage]);

  // Shared save: PUTs the FULL tenant object regardless of which tab triggered
  // it. This is what keeps the full-column backend update safe.
  const handleSave = async (e) => {
    if (e && e.preventDefault) e.preventDefault();

    if (!userInfo?.tenant_id) {
      setMessage("Session error. Please refresh the page.");
      return;
    }

    try {
      const payload = {
        ...formData,
        phone_number: formData.phone_number.replace(/\D/g, ""),
        state_id: formData.state_id || null,
        enable_invoices: formData.enable_invoices,
        invoice_number_prefix:
          formData.invoice_number_prefix?.toUpperCase() || "INV",
        invoice_notes: formData.invoice_notes || "",
      };

      await secureAxios.put(`/api/tenants/${userInfo.tenant_id}`, payload);
      setMessage("Tenant information updated successfully.");
    } catch (err) {
      console.error("Failed to update tenant:", err);
      setMessage("Failed to update tenant information.");
    }
  };

  const handleChange = (e) => {
    const { name, value, type, checked } = e.target;

    if (type === "checkbox") {
      setFormData((prev) => ({ ...prev, [name]: checked }));
    } else if (name === "phone_number") {
      const digits = value.replace(/\D/g, "").slice(0, 10);
      setFormData((prev) => ({ ...prev, phone_number: formatPhone(digits) }));
    } else if (name === "invoice_number_prefix") {
      const cleaned = value
        .replace(/[^a-zA-Z0-9]/g, "")
        .slice(0, 10)
        .toUpperCase();
      setFormData((prev) => ({ ...prev, invoice_number_prefix: cleaned }));
    } else {
      setFormData((prev) => ({ ...prev, [name]: value }));
    }
  };

  const handleLogoUpload = async (e) => {
    const file = e.target.files[0];
    if (!file) return;

    const validTypes = ["image/jpeg", "image/png", "image/gif", "image/webp"];
    if (!validTypes.includes(file.type)) {
      setLogoUploadMessage(
        "Please upload a valid image file (JPEG, PNG, GIF, or WebP)."
      );
      return;
    }

    const maxSize = 5 * 1024 * 1024;
    if (file.size > maxSize) {
      setLogoUploadMessage("File size must be less than 5MB.");
      return;
    }

    if (!userInfo?.tenant_id) {
      setLogoUploadMessage("Session error. Please refresh the page.");
      return;
    }

    setUploadingLogo(true);
    const formDataUpload = new FormData();
    formDataUpload.append("logo", file);

    try {
      const res = await secureAxios.post(
        `/api/tenants/${userInfo.tenant_id}/logo`,
        formDataUpload
      );
      setFormData((prev) => ({ ...prev, logo_url: res.data.logo_url }));
      await fetchLogoUrl(res.data.logo_url, userInfo.tenant_id);
      setLogoUploadMessage("Logo uploaded successfully.");
    } catch (err) {
      console.error("Logo upload failed:", err);
      setLogoUploadMessage("Logo upload failed. Please try again.");
    } finally {
      setUploadingLogo(false);
    }
  };

  // Shared messages shown above tab content (relevant to general + invoice).
  const renderMessages = () => (
    <>
      {logoUploadMessage && (
        <div
          className={`alert alert-dismissible fade show ${
            logoUploadMessage.toLowerCase().includes("success")
              ? "alert-success"
              : "alert-danger"
          }`}
        >
          {logoUploadMessage}
          <button
            type="button"
            className="btn-close"
            onClick={() => setLogoUploadMessage("")}
            aria-label="Close"
          ></button>
        </div>
      )}
      {message && (
        <div
          className={`alert alert-dismissible fade show ${
            message.toLowerCase().includes("success")
              ? "alert-success"
              : "alert-danger"
          }`}
        >
          {message}
          <button
            type="button"
            className="btn-close"
            onClick={() => setMessage("")}
            aria-label="Close"
          ></button>
        </div>
      )}
    </>
  );

  const renderFormTab = (tab) => {
    if (loading) {
      return (
        <div className="text-center py-5">
          <div className="spinner-border text-primary" role="status">
            <span className="visually-hidden">Loading...</span>
          </div>
          <p className="mt-2">Loading tenant information...</p>
        </div>
      );
    }
    if (error) {
      return (
        <div className="alert alert-danger">
          <i className="bi bi-exclamation-triangle-fill me-2"></i>
          {error}
        </div>
      );
    }
    if (tab === "general") {
      return (
        <>
          {renderMessages()}
          <TenantGeneralInfoTab
            formData={formData}
            states={states}
            onChange={handleChange}
            onSave={handleSave}
            displayLogoUrl={displayLogoUrl}
            uploadingLogo={uploadingLogo}
            onLogoUpload={handleLogoUpload}
          />
        </>
      );
    }
    if (tab === "invoice") {
      return (
        <>
          {renderMessages()}
          <TenantInvoiceSettingsTab
            formData={formData}
            onChange={handleChange}
            onSave={handleSave}
          />
        </>
      );
    }
    return null;
  };

  return (
    <div className="container mt-4">
      <div className="card">
        <div className="card-body">
          <h4 className="card-title mb-4">
            <i className="bi bi-building me-2"></i>
            Tenant Settings
          </h4>

          <ul className="nav nav-tabs mb-3">
            <li className="nav-item">
              <button
                type="button"
                className={`nav-link ${activeTab === "general" ? "active" : ""}`}
                onClick={() => setActiveTab("general")}
              >
                <i className="bi bi-info-circle me-1"></i>
                Tenant Information
              </button>
            </li>
            <li className="nav-item">
              <button
                type="button"
                className={`nav-link ${activeTab === "invoice" ? "active" : ""}`}
                onClick={() => setActiveTab("invoice")}
              >
                <i className="bi bi-receipt me-1"></i>
                Invoice Settings
              </button>
            </li>
            <li className="nav-item">
              <button
                type="button"
                className={`nav-link ${activeTab === "email" ? "active" : ""}`}
                onClick={() => setActiveTab("email")}
              >
                <i className="bi bi-envelope-at me-1"></i>
                Email Sender
              </button>
            </li>
          </ul>

          {activeTab === "general" && renderFormTab("general")}
          {activeTab === "invoice" && renderFormTab("invoice")}
          {activeTab === "email" && <TenantEmailSenderTab />}
        </div>
      </div>
    </div>
  );
};

export default TenantInfoForm;